<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { type Locale, loadLanguageAsync } from '../../i18n'

const { locale } = useI18n()
const currentLocale = ref(locale.value)

const availableLocales = [
  { code: 'en' as Locale, name: 'English' },
  { code: 'zh-TW' as Locale, name: '繁體中文 (台灣)' },
  { code: 'zh-HK' as Locale, name: '繁體中文 (香港)' },
  { code: 'zh-CN' as Locale, name: '简体中文' },
  { code: 'ja' as Locale, name: '日本語' },
  { code: 'ko' as Locale, name: '한국어' }
]

// Find the best match between browser language and our available languages
const findBestLanguageMatch = (browserLang: string): Locale => {

  // Direct match for full locale code (e.g., zh-TW)
  const directMatch = availableLocales.find(l => l.code === browserLang)
  if (directMatch) return directMatch.code
  
  // Match by language part (e.g., "zh" from "zh-CN")
  const langPart = browserLang.split('-')[0]
  
  // For Chinese, we provide better defaults based on regional preferences
  if (langPart === 'zh') {
    if (browserLang === 'zh-TW') return 'zh-TW'
    if (browserLang === 'zh-HK') return 'zh-HK'
    return 'zh-CN' // Default Chinese to Simplified
  }
  
  // For other languages, match by language code
  const langMatch = availableLocales.find(l => l.code.startsWith(langPart))
  if (langMatch) return langMatch.code
  
  // If no match, return English as default
  return 'en'
}

onMounted(() => {
    // Auto-detect from browser language preferences for optimal user experience
    const browserLang = navigator.language || (navigator.languages && navigator.languages[0]) || 'en'

    // Find best match from available languages using intelligent matching
    const bestMatch = findBestLanguageMatch(browserLang)
    
    currentLocale.value = bestMatch
    // Save to localStorage but don't mark as manual selection
    localStorage.setItem('user-locale', bestMatch)
  
  // Set HTML lang attribute
  document.querySelector('html')?.setAttribute('lang', currentLocale.value)
  
  // Load the language asynchronously with error handling
  loadLanguageAsync(currentLocale.value as Locale).catch(err => {
    console.error('Failed to load locale:', err instanceof Error ? err.message : 'Unknown error')
  })
})

</script> 